import type { ChangeEvent, DragEvent as ReactDragEvent } from "react"
import { useEffect, useRef, useState } from "react"
import { useI18n } from "../i18n/useI18n"
import {
  type ArtworkAssetStore,
  createLibraryArchiveBlob,
  createLibraryArchiveFilename,
  type LibraryArchiveKind,
  type LibraryStoreByKind,
  LibraryTransferError,
  mergeImportedRecords,
  parseLibraryArchiveFile,
} from "../library/archive"
import { upsertArtworkAssets } from "../media/assets"

type LibraryRecordByKind<TKind extends LibraryArchiveKind> =
  LibraryStoreByKind[TKind][number]

type StoredStateSetter<T> = (next: T | ((previous: T) => T)) => void

type StatusTone = "danger" | "success"

type TransferStatus = {
  message: string
  tone: StatusTone
}

type UseLibraryTransferOptions<TKind extends LibraryArchiveKind> = {
  kind: TKind
  label: string
  records: LibraryStoreByKind[TKind]
  setRecords: StoredStateSetter<LibraryStoreByKind[TKind]>
  getId: (record: LibraryRecordByKind<TKind>) => string
  // Cards-only: artwork assets to include in the backup and restore on import
  artworkAssets?: ArtworkAssetStore
  setArtworkAssets?: StoredStateSetter<ArtworkAssetStore>
}

function isFileDrag(
  event: Pick<DragEvent, "dataTransfer"> | Pick<ReactDragEvent, "dataTransfer">,
): boolean {
  return [...(event.dataTransfer?.types ?? [])].includes("Files")
}

export function useLibraryTransfer<TKind extends LibraryArchiveKind>({
  kind,
  label,
  records,
  setRecords,
  getId,
  artworkAssets,
  setArtworkAssets,
}: UseLibraryTransferOptions<TKind>) {
  const { t } = useI18n()
  const inputRef = useRef<HTMLInputElement>(null)
  const dragDepthRef = useRef(0)
  const recordsRef = useRef(records)
  const artworkAssetsRef = useRef(artworkAssets)
  const [isBusy, setIsBusy] = useState(false)
  const [isDragActive, setIsDragActive] = useState(false)
  const [status, setStatus] = useState<TransferStatus | null>(null)

  useEffect(() => {
    recordsRef.current = records
  }, [records])

  useEffect(() => {
    artworkAssetsRef.current = artworkAssets
  }, [artworkAssets])

  const resetDragState = () => {
    dragDepthRef.current = 0
    setIsDragActive(false)
  }

  const openFilePicker = () => {
    if (isBusy) return
    inputRef.current?.click()
  }

  const handleImportFile = async (file: File | null) => {
    resetDragState()
    if (!file || isBusy) return

    setIsBusy(true)
    setStatus(null)

    try {
      const archive = await parseLibraryArchiveFile(file, kind)
      const mergeResult = mergeImportedRecords({
        existingRecords: recordsRef.current,
        getId,
        importedRecords: archive.records,
      })

      setRecords(mergeResult.records as LibraryStoreByKind[TKind])
      recordsRef.current = mergeResult.records as LibraryStoreByKind[TKind]

      // Restore artwork assets bundled with the archive (cards only)
      if (
        "artworkAssets" in archive &&
        Array.isArray(archive.artworkAssets) &&
        archive.artworkAssets.length > 0 &&
        setArtworkAssets
      ) {
        setArtworkAssets((previous) =>
          upsertArtworkAssets(previous ?? [], archive.artworkAssets ?? []),
        )
      }

      setStatus({
        message: t("table.transfer.uploadSuccess", {
          added: mergeResult.addedCount,
          skipped: mergeResult.skippedCount,
          updated: mergeResult.updatedCount,
        }),
        tone: "success",
      })
    } catch (error) {
      setStatus({
        message: getTransferErrorMessage(error),
        tone: "danger",
      })
    } finally {
      setIsBusy(false)
    }
  }

  const downloadBackup = async (
    recordsOverride?: LibraryStoreByKind[TKind],
  ) => {
    if (isBusy) return

    setIsBusy(true)
    setStatus(null)

    try {
      const recordsToExport = recordsOverride ?? recordsRef.current

      // Collect only the artwork assets referenced by the exported records
      const assetsToExport =
        (kind === "cards" || kind === "heroes") && artworkAssetsRef.current
          ? collectReferencedArtworkAssets(
              kind,
              recordsToExport as
                | LibraryStoreByKind["cards"]
                | LibraryStoreByKind["heroes"],
              artworkAssetsRef.current,
            )
          : undefined

      const blob = await createLibraryArchiveBlob(
        kind,
        recordsToExport,
        assetsToExport,
      )
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.download = createLibraryArchiveFilename(kind)
      anchor.href = url
      document.body.append(anchor)
      anchor.click()
      anchor.remove()
      setTimeout(() => URL.revokeObjectURL(url), 30_000)

      setStatus({
        message: t("table.transfer.downloadSuccess", {
          count: recordsToExport.length,
        }),
        tone: "success",
      })
    } catch (error) {
      setStatus({
        message: getTransferErrorMessage(error),
        tone: "danger",
      })
    } finally {
      setIsBusy(false)
    }
  }

  const getTransferErrorMessage = (error: unknown): string => {
    if (error instanceof LibraryTransferError) {
      switch (error.code) {
        case "LIBRARY_TRANSFER_COMPRESSION_UNAVAILABLE":
          return t("table.transfer.error.compressionUnsupported")
        case "LIBRARY_TRANSFER_KIND_MISMATCH":
          return t("table.transfer.error.kindMismatch", {
            actual:
              error.actualKind == null
                ? t("common.unknown")
                : getKindLabel(error.actualKind),
            expected: label,
          })
        case "LIBRARY_TRANSFER_INVALID_ARCHIVE":
          return t("table.transfer.error.invalidArchive")
      }
    }

    return error instanceof Error
      ? error.message
      : t("table.transfer.error.generic")
  }

  const getKindLabel = (value: LibraryArchiveKind): string => {
    switch (value) {
      case "cards":
        return t("table.cards.title")
      case "heroes":
        return t("table.heroes.title")
      case "decks":
        return t("table.decks.title")
    }
  }

  return {
    downloadBackup,
    inputRef,
    isBusy,
    isDragActive,
    onDragEnter(event: ReactDragEvent<HTMLElement>) {
      if (!isFileDrag(event)) return
      event.preventDefault()
      if (isBusy) return
      dragDepthRef.current += 1
      setIsDragActive(true)
    },
    onDragLeave(event: ReactDragEvent<HTMLElement>) {
      if (!isFileDrag(event)) return
      event.preventDefault()
      if (isBusy) return
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
      if (dragDepthRef.current === 0) {
        setIsDragActive(false)
      }
    },
    onDragOver(event: ReactDragEvent<HTMLElement>) {
      if (!isFileDrag(event)) return
      event.preventDefault()
      if (isBusy) return
      event.dataTransfer.dropEffect = "copy"
    },
    onDrop(event: ReactDragEvent<HTMLElement>) {
      if (!isFileDrag(event)) return
      event.preventDefault()
      const file = event.dataTransfer.files?.[0] ?? null
      void handleImportFile(file)
    },
    onInputChange(event: ChangeEvent<HTMLInputElement>) {
      const file = event.currentTarget.files?.[0] ?? null
      event.currentTarget.value = ""
      void handleImportFile(file)
    },
    openFilePicker,
    status,
  }
}

/**
 * Returns only the artwork asset records referenced by the exported records.
 * Used to avoid bundling unused assets in the backup.
 */
function collectReferencedArtworkAssets(
  kind: "cards" | "heroes",
  records: LibraryStoreByKind["cards"] | LibraryStoreByKind["heroes"],
  assets: ArtworkAssetStore,
): ArtworkAssetStore {
  const referencedIds = new Set<string>()
  if (kind === "cards") {
    for (const record of records as LibraryStoreByKind["cards"]) {
      for (const variant of record.card.artwork?.variants ?? []) {
        referencedIds.add(variant.assetId)
      }
    }
  } else {
    for (const record of records as LibraryStoreByKind["heroes"]) {
      for (const level of record.hero.progression) {
        for (const variant of level.card.artwork?.variants ?? []) {
          referencedIds.add(variant.assetId)
        }
      }
    }
  }
  return assets.filter((asset) => referencedIds.has(asset.assetId))
}
